// https://geoffrich.net/posts/svelte-action-sticky/
// https://svelte.dev/repl/4ad71e00c86c47d29806e17f09ff0869?version=3.59.2
/**
 * Svelte action that dispatches a custom stuck event when a node becomes stuck or unstuck (position: sticky is having an effect)
 * @param {HTMLElement} node  - the node the action is placed on
 * @param {Object} options - action options
 * @param {boolean} [options.earlyReturn] - disable sticky action
 * @param {number} [options.offset] - offset (e.g sticky header+banners height)
 * @param {boolean} [options.stickToTop] - position where the node is stuck (false = bottom)
 // * @param params.callback - function to execute when the node becomes stuck or unstuck
 */
export const stickyAction = (node, { earlyReturn = false, offset, stickToTop = true }) => {
	if (earlyReturn) return;

	let stickySentinelTop;
	let stickySentinelBottom;

	const intersectionCallback = function (entries) {
		// only observing one item at a time
		const entry = entries[0];

		let isStuck = false;
		if (!entry.isIntersecting && isValidYPosition(entry)) {
			isStuck = true;
		}

		node.dispatchEvent(
			new CustomEvent('stuck', {
				detail: { isStuck }
			})
		);
	};

	const isValidYPosition = function ({ target, boundingClientRect }) {
		if (target === stickySentinelTop) {
			return boundingClientRect.y < 0;
		} else {
			return boundingClientRect.y > 0;
		}
	};

	const mutationCallback = function (mutations) {
		// If something changes and the sentinel nodes are no longer first and last child, put them back in position
		mutations.forEach(function (mutation) {
			if (stickySentinelTop) {
				const { parentNode: topParent } = stickySentinelTop;
				if (stickySentinelTop !== topParent.firstChild) {
					topParent.prepend(stickySentinelTop);
				}
			}

			if (stickySentinelBottom) {
				const { parentNode: bottomParent } = stickySentinelBottom;
				if (stickySentinelBottom !== bottomParent.lastChild) {
					bottomParent.append(stickySentinelBottom);
				}
			}
		});
	};
	let intersectionObserver = new IntersectionObserver(intersectionCallback, {
		threshold: 1
	});
	const mutationObserver = new MutationObserver(mutationCallback);

	if (stickToTop) {
		// we insert and observe a sentinel node immediately after the target
		// when it is visible, the target node cannot be sticking
		stickySentinelTop = document.createElement('div');
		stickySentinelTop.style.position = 'absolute';
		if (offset) {
			stickySentinelTop.style.height = `${offset}px`;
			stickySentinelTop.style.width = `1px`;
			stickySentinelTop.style.transform = `translateY(-${offset}px)`;
		}

		stickySentinelTop.classList.add('stickySentinelTop');
		// without setting a height, Safari breaks
		node.insertAdjacentElement('beforebegin', stickySentinelTop);

		intersectionObserver.observe(stickySentinelTop);
	}

	if (!stickToTop) {
		stickySentinelBottom = document.createElement('div');
		stickySentinelBottom.classList.add('stickySentinelBottom');

		node.insertAdjacentElement('afterend', stickySentinelBottom);
		intersectionObserver.observe(stickySentinelBottom);
	}

	mutationObserver.observe(node, { childList: true });

	return {
		update({ stickToTop }) {
			// change which sentinel we are observing
			if (stickToTop) {
				stickySentinelBottom && intersectionObserver.unobserve(stickySentinelBottom);
				stickySentinelTop && intersectionObserver.observe(stickySentinelTop);
			} else {
				stickySentinelTop && intersectionObserver.unobserve(stickySentinelTop);
				stickySentinelBottom && intersectionObserver.observe(stickySentinelBottom);
			}
		},

		destroy() {
			intersectionObserver.disconnect();
			mutationObserver.disconnect();
		}
	};
};
